"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "@/lib/analytics/useAnalytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { usePostHog } from "posthog-js/react";
import { Button, PhoneInput, normalizePhoneInputValue } from "@artha/ui";
import {
  TalentNetworkPhoneSignInRequestSchema,
  TalentNetworkPhoneSignInVerifySchema,
} from "@artha/api-contracts";
import type { TalentNetworkPhoneSignInResponseDto } from "@artha/api-contracts";
import { Pencil } from "lucide-react";
import {
  requestTalentNetworkPhoneSignIn,
  verifyTalentNetworkPhoneSignIn,
} from "../../services/contactVerificationClient";
import { PersonalizationApiError } from "../../services/personalizationClient";
import { clearTalentNetworkDraft } from "../../../talent-network/services/talentNetworkDraft";
import {
  PERSONALIZATION_PRIMARY_ACTION_CLASS_NAME,
  TALENT_NETWORK_UPLOAD_COPY,
} from "../../personalization.constants";
import {
  CONTACT_EDIT_BUTTON_CLASS_NAME,
  CONTACT_EDIT_ICON_CLASS_NAME,
  CONTACT_TIMER_INTERVAL_MS,
} from "../verification/verification.constants";
import type {
  PhoneSignInAction,
  PhoneSignInStepProps,
} from "../verification/verification.types";
import { OtpStep } from "./OtpStep";
import messages from "@/i18n/messages/en.json";

export function PhoneSignInStep({
  username,
  creatorId,
  creatorName: _creatorName,
  onComplete,
  onUploadResume,
}: PhoneSignInStepProps) {
  const { trackEvent } = useAnalytics();
  const posthog = usePostHog();
  const id = useId();
  const [phone, setPhone] = useState("");
  const [challenge, setChallenge] =
    useState<TalentNetworkPhoneSignInResponseDto | null>(null);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<PhoneSignInAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    if (!resendAt) return;
    const timer = window.setInterval(
      () => setNow(Date.now()),
      CONTACT_TIMER_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [resendAt]);
  const wait = Math.max(
    0,
    Math.ceil((resendAt - now) / CONTACT_TIMER_INTERVAL_MS),
  );

  const send = async () => {
    if (pending || (challenge && wait)) return;
    if (!username) {
      setError("Creator unavailable. Please reopen the Talent Network page.");
      return;
    }
    const normalized = normalizePhoneInputValue(phone, "IN");
    if (
      !TalentNetworkPhoneSignInRequestSchema.safeParse({
        phone: normalized,
        consent: true,
      }).success
    ) {
      setError("Enter a valid phone number with its country code.");
      return;
    }
    setPending("sending");
    setError(null);
    setNotFound(false);
    try {
      const result = await requestTalentNetworkPhoneSignIn(username, {
        phone: normalized,
        consent: true,
      });
      if (!active.current) return;
      setPhone(normalized);
      setChallenge(result);
      setCode("");
      setNow(Date.now());
      setResendAt(
        Date.now() + result.resendAfterSeconds * CONTACT_TIMER_INTERVAL_MS,
      );
    } catch (error) {
      if (!active.current) return;
      setError(
        error instanceof Error
          ? error.message
          : "We could not send the code. Please try again.",
      );
      setNotFound(
        error instanceof PersonalizationApiError &&
          error.code === "CANDIDATE_PROFILE_NOT_FOUND",
      );
    } finally {
      if (active.current) setPending(null);
    }
  };

  const verify = async () => {
    if (pending || !challenge) return;
    const input = { phone, code, challengeToken: challenge.challengeToken };
    if (!TalentNetworkPhoneSignInVerifySchema.safeParse(input).success) {
      setError("Enter the six-digit verification code.");
      return;
    }
    setPending("verifying");
    setError(null);
    try {
      const result = await verifyTalentNetworkPhoneSignIn(username, input);
      if (!active.current) return;
      if (!result.authenticated)
        throw new Error("Sign-in could not be completed. Please try again.");
      if (result.externalId) posthog?.identify?.(result.externalId);
      const properties = {
        portal_id: creatorId, username, source: "talent_network", method: "phone_otp",
        candidate_external_id: result.externalId, has_resume: result.hasResume,
      };
      trackEvent(ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, { ...properties, mode: "login" });
      trackEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGIN, properties);
      clearTalentNetworkDraft(username);
      await onComplete();
    } catch (error) {
      if (active.current)
        setError(
          error instanceof Error
            ? error.message
            : "We could not verify the code. Please try again.",
        );
    } finally {
      if (active.current) setPending(null);
    }
  };

  return (
    <div className="space-y-4">
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {challenge ? (
        <OtpStep
          code={code}
          email=""
          sentMessage={
            <>
              {messages.talentNetworkUi.enter_the_code_sent_to}<span className="whitespace-nowrap">{phone}</span>
              <button
                type="button"
                aria-label="Edit phone number"
                disabled={Boolean(pending)}
                className={CONTACT_EDIT_BUTTON_CLASS_NAME}
                onClick={() => {
                  setChallenge(null);
                  setCode("");
                  setError(null);
                }}
              >
                <Pencil className={CONTACT_EDIT_ICON_CLASS_NAME} aria-hidden="true" />
              </button>
            </>
          }
          submitLabel="Verify & join network"
          isRequestingCode={pending === "sending"}
          isVerifying={pending === "verifying"}
          onCodeChange={setCode}
          onSubmit={() => void verify()}
          onResend={() => void send()}
          resendLabel={wait ? `Resend (${wait}s)` : "Resend"}
          resendDisabled={wait > 0}
        />
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <PhoneInput
            id={`${id}-phone`}
            value={phone}
            onChange={setPhone}
            defaultCountry="IN"
            label="Phone number"
            countryLabel="Country code"
            searchPlaceholder="Search countries"
            emptyLabel="No countries found"
            disabled={Boolean(pending)}
            description={messages.common.phoneOtpHint}
          />
          <p className="text-sm text-[#667085]">
            By continuing, you agree to artha.link&apos;s{" "}
            <a
              href={TALENT_NETWORK_UPLOAD_COPY.termsUrl}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[#344054]"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href={TALENT_NETWORK_UPLOAD_COPY.privacyUrl}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[#344054]"
            >
              Privacy Policy
            </a>
            .
          </p>
          <Button
            type="submit"
            className={PERSONALIZATION_PRIMARY_ACTION_CLASS_NAME}
            disabled={Boolean(pending)}
          >
            {pending ? "Sending code..." : "Send OTP"}
          </Button>
          {notFound ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onUploadResume}
            >
              Upload a resume to join
            </Button>
          ) : null}
        </form>
      )}
    </div>
  );
}
